import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import "./EditAlumniFormPage.css";
import {
  titles,
  genders,
  maritalStatuses,
  residenceHalls,
  associateSchools,
  academicStatuses,
  professionalStatuses,
  getYearOptions,
} from "../../../utils/options";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

export default function EditAlumniFormPage() {
  const navigate = useNavigate();
  const { id: alumniId } = useParams(); // ✅ Grab alumniId from URL
  const [step, setStep] = useState(1);
  const years = getYearOptions(50);

  // ✅ Only fullname of current user
  const [currentUser, setCurrentUser] = useState({ fullname: "" });
   useEffect(() => {
  const user = JSON.parse(localStorage.getItem("user")); // ✅ correct key
  if (user?.fullname) setCurrentUser({ fullname: user.fullname });
}, []);


  const [formData, setFormData] = useState({
    alumniID: "",
    editedBy: "", // Will be set after currentUser loads
    title: "",
    firstName: "",
    middleName: "",
    lastName: "",
    gender: "",
    dateOfBirth: "",
    mobileNumbers: ["231", "231"],
    email: "",
    homeTelephone: "231",
    address: "",
    maritalStatus: "",
    residenceHall: "",
    yearOfAdmission: "",
    yearOfGraduation: "",
    areaOfStudy: "",
    associateSchool: "",
    academicStatus: "",
    professionalStatus: "",
    dependents: [
      { name: "", relationship: "", dateOfBirth: "", location: "" },
      { name: "", relationship: "", dateOfBirth: "", location: "" },
      { name: "", relationship: "", dateOfBirth: "", location: "" },
      { name: "", relationship: "", dateOfBirth: "", location: "" },
    ],
    nextOfKinName: "",
    emergencyContactName: "",
    emergencyContactPhone: "231",
    agreedToPolicies: false,
  });

  // ✅ Update editedBy when currentUser loads
  useEffect(() => {
    if (currentUser.fullname) {
      setFormData((prev) => ({ ...prev, editedBy: currentUser.fullname }));
    }
  }, [currentUser]);

  useEffect(() => {
    const fetchAlumni = async () => {
      if (!alumniId) return;
      try {
        const response = await axios.get(`${API_BASE_URL}/current-alumni/${alumniId}`);
        const alumni = response.data;

        const mobileNumbers = alumni.mobileNumbers?.length
          ? alumni.mobileNumbers
          : ["231", "231"];
        const dependents = alumni.dependents?.length
          ? alumni.dependents
          : formData.dependents;

        setFormData((prev) => ({
          ...prev,
          ...alumni,
          mobileNumbers,
          dependents,
          editedBy: currentUser.fullname || prev.editedBy,
        }));
      } catch (err) {
        console.error(err);
        alert("Failed to load alumni data.");
      }
    };

    fetchAlumni();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alumniId, currentUser.fullname]);

  const nextStep = () => setStep((prev) => Math.min(prev + 1, 3));
  const prevStep = () => setStep((prev) => Math.max(prev - 1, 1));

  const handleChange = (e, dependentIndex) => {
    const { name, value, type, checked } = e.target;

    if (name.startsWith("dependent") && dependentIndex !== undefined) {
      const updatedDependents = [...formData.dependents];
      const field = name.split("-")[1];
      updatedDependents[dependentIndex][field] = value;
      setFormData({ ...formData, dependents: updatedDependents });
      return;
    }

    if (type === "checkbox") {
      setFormData({ ...formData, [name]: checked });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handlePhoneChange = (value, field, mobileIndex) => {
    const countryCodes = ["231", "233"];
    countryCodes.forEach((code) => {
      if (value.startsWith(code + "0")) {
        value = code.slice(0, code.length) + value.slice(code.length + 1);
      }
    });

    if (field === "mobile") {
      const updatedMobiles = [...formData.mobileNumbers];
      updatedMobiles[mobileIndex] = value;
      setFormData({ ...formData, mobileNumbers: updatedMobiles });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step < 3) {
      nextStep();
      return;
    }

    if (!alumniId) {
      alert("Invalid alumni ID.");
      return;
    }

    try {
      const payload = { ...formData, dateEdited: new Date().toISOString() };
      await axios.put(`${API_BASE_URL}/current-alumni/${alumniId}`, payload);
      alert("Alumni updated successfully!");
      navigate(-1);
    } catch (err) {
      console.error(err);
      alert("Failed to update alumni.");
    }
  };

  return (
    <div className="edit-alumni-page">
      <section className="edit-hero">
              <button
  className="back-btn"
  onClick={() => navigate(-1)}
  style={{ 
    backgroundColor: "black", 
    color: "white",           
    float: "left",            
    padding: "8px 16px",      
    border: "none",           
    borderRadius: "4px",     
    cursor: "pointer"         
  }}
>
  ← Back
</button>

        <h1>Edit Alumni Details</h1>
        <p>Alumni ID: <span className="alumni-id">{formData.alumniID}</span></p>
      </section>

      <section className="edit-form-section">
        <form className="edit-form" onSubmit={handleSubmit}>
          <AnimatePresence exitBeforeEnter>
            
                  {/* Step 1: Personal Info */}
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="edit-form-step"
              >
                <h2>Personal Information</h2>
                  <div className="edit-input-group">
                  <label>Edited By</label>
                  <input type="text" value={formData.editedBy} disabled />
                </div>

                <div className="edit-input-group">
                  <label>Title</label>
                  <select
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Title</option>
                    {titles.map((t, i) => (
                      <option key={i} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="edit-input-group">
                  <label>Last Name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="edit-input-group">
                  <label>First Name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="edit-input-group">
                  <label>Middle Name</label>
                  <input
                    type="text"
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleChange}
                  />
                </div>

                <div className="edit-input-group">
                  <label>Gender</label>
                  <select
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select Gender</option>
                    {genders.map((g, i) => (
                      <option key={i} value={g}>
                        {g}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="edit-input-group">
                  <label>Date of Birth</label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth?.slice(0, 10)}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="edit-input-group">
                  <label>Mobile Numbers</label>
                  {formData.mobileNumbers.map((mob, idx) => (
                    <PhoneInput
                      key={idx}
                      country={"lr"}
                      value={mob}
                      onChange={(value) => handlePhoneChange(value, "mobile", idx)}
                      inputProps={{ name: `mobile-${idx}`, required: idx === 0 }}
                      inputStyle={{ width: "100%" }}
                    />
                  ))}
                </div>

                <div className="edit-input-group">
                  <label>Email Address</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="edit-input-group">
                  <label>Home Telephone</label>
                  <PhoneInput
                    country={"lr"}
                    value={formData.homeTelephone}
                    onChange={(value) => handlePhoneChange(value, "homeTelephone")}
                    inputProps={{ name: "homeTelephone" }}
                    inputStyle={{ width: "100%" }}
                  />
                </div>

                <div className="edit-input-group">
                  <label>Address</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                  />
                </div>

                <div className="edit-input-group">
                  <label>Marital Status</label>
                  <select
                    name="maritalStatus"
                    value={formData.maritalStatus}
                    onChange={handleChange}
                  >
                    <option value="">Select</option>
                    {maritalStatuses.map((m, i) => (
                      <option key={i} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

              </motion.div>
            )}

            {/* Step 2: University Info */}
            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="edit-form-step"
              >
                <h2>University Information</h2>
                   <div className="reg-input-group">
                  <label>Residence Hall at UG-Legon</label>
                  <select name="residenceHall" value={formData.residenceHall} onChange={handleChange} required>
                    <option value="">Select Hall</option>
                    {residenceHalls.map((h, i) => <option key={i} value={h}>{h}</option>)}
                  </select>
                </div>

                <div className="reg-input-group">
                  <label>Year of Admission</label>
                  <select name="yearOfAdmission" value={formData.yearOfAdmission} onChange={handleChange} required>
                    <option value="">Select Year</option>
                    {years.map((y, i) => <option key={i} value={y}>{y}</option>)}
                  </select>
                </div>

                <div className="reg-input-group">
                  <label>Year of Graduation</label>
                  <select name="yearOfGraduation" value={formData.yearOfGraduation} onChange={handleChange} required>
                    <option value="">Select Year</option>
                    {years.map((y, i) => <option key={i} value={y}>{y}</option>)}
                  </select>
                </div>

                <div className="reg-input-group">
                  <label>Area of Study at UG</label>
                  <input type="text" name="areaOfStudy" value={formData.areaOfStudy} onChange={handleChange} required />
                </div>

                <div className="reg-input-group">
                  <label>For Associate Members - School Graduated From</label>
                  <select name="associateSchool" value={formData.associateSchool} onChange={handleChange}>
                    <option value="">Select School</option>
                    {associateSchools.map((s, i) => <option key={i} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="reg-input-group">
                  <label>Current Academic Status</label>
                  <select name="academicStatus" value={formData.academicStatus} onChange={handleChange}>
                    <option value="">Select Status</option>
                    {academicStatuses.map((s, i) => <option key={i} value={s}>{s}</option>)}
                  </select>
                </div>

                <div className="reg-input-group">
                  <label>Current Professional Status</label>
                  <select name="professionalStatus" value={formData.professionalStatus} onChange={handleChange}>
                    <option value="">Select Status</option>
                    {professionalStatuses.map((s, i) => <option key={i} value={s}>{s}</option>)}
                  </select>
                </div>

              </motion.div>
            )}

            {/* Step 3: Dependents & Emergency Contact */}
            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                className="edit-form-step"
              >
                <h2>Dependents & Emergency Contact</h2>

                {formData.dependents.map((dep, i) => (
                  <div key={i} className="reg-input-group reg-dependents">
                    <label>Dependent {i + 1}</label>
                    <input
                      type="text"
                      name={`dependent-name`}
                      value={dep.name}
                      onChange={(e) => handleChange(e, i)}
                      placeholder="Name"
                    />
                    <input
                      type="text"
                      name={`dependent-relationship`}
                      value={dep.relationship}
                      onChange={(e) => handleChange(e, i)}
                      placeholder="Relationship"
                    />
                    <input
                      type="date"
                      name={`dependent-dateOfBirth`}
                      value={dep.dateOfBirth}
                      onChange={(e) => handleChange(e, i)}
                      placeholder="Date of Birth"
                    />
                    <input
                      type="text"
                      name={`dependent-location`}
                      value={dep.location}
                      onChange={(e) => handleChange(e, i)}
                      placeholder="Location"
                    />
                  </div>
                ))}

                <div className="reg-input-group">
                  <label>Next of Kin Name</label>
                  <input
                    type="text"
                    name="nextOfKinName"
                    value={formData.nextOfKinName}
                    onChange={handleChange}
                    placeholder="Next of Kin"
                    required
                  />
                </div>

                <div className="reg-input-group">
                  <label>Emergency Contact Name & Number</label>
                  <input
                    type="text"
                    name="emergencyContactName"
                    value={formData.emergencyContactName}
                    onChange={handleChange}
                    placeholder="Name"
                    required
                  />
                  <PhoneInput
                    country={"lr"}
                    value={formData.emergencyContactPhone}
                    onChange={(value) =>
                      handlePhoneChange(value, "emergencyContactPhone")
                    }
                    inputProps={{
                      name: "emergencyContactPhone",
                      required: true,
                    }}
                    inputStyle={{ width: "100%" }}
                  />
                </div>
              </motion.div>
            )}





          </AnimatePresence>

          <div className="edit-form-navigation">
            {step > 1 && (
              <button type="button" className="edit-secondary-btn" onClick={prevStep}>
                Previous
              </button>
            )}
            {step < 3 && (
              <button type="button" className="edit-primary-btn" onClick={nextStep}>
                Next
              </button>
            )}
            {step === 3 && (
              <button type="submit" className="edit-primary-btn">
                Update Alumni
              </button>
            )}
          </div>
        </form>
      </section>
    </div>
  );
}
